import type { FiatPrice } from "@b2bportal/air-booking-api";
import { useGetExperimentVariant } from "@hopper-b2b/experiments"; // Initial + April Experiment Flag
import dayjs from "dayjs";

// Initial + April Experiment Flag
// export const FTU_EXPERIMENT_KEY = "uber-first-time-user-promo";
// July/August FTU Experiment Flag
// export const FTU_EXPERIMENT_KEY = "ftu-july-august";
// Sept/Oct FTU Experiment Flag
// export const FTU_EXPERIMENT_KEY = "ftu-sept-oct";
// Nov/Dec FTU Experiment Flag
// export const FTU_EXPERIMENT_KEY = "ftu-nov-dec";
// Feb/Apr FTU Experiment Flag
export const FTU_EXPERIMENT_KEY = "ftu-feb-apr";

export enum FTU_VARIANTS {
  control = "Control",
  // TwentyPoundsPerPassenger = "TwentyPoundsPerPassenger",
  // TwentyPoundsPerTransaction = "TwentyPoundsPerTransaction",
  // TenPoundsPerPassenger = "TenPoundsPerPassenger",
  // TenPercent = "TenPercent",
  // AprilLuckyDraw = "AprilLuckyDraw",
  // -> July/August FTU Experiments
  // TwentyPercentPerPax = "TwentyPercentPerPax",
  // TwentyPoundsPerPax = "TwentyPoundsPerPax",
  // -> Sept/Oct FTU Experiments
  // TwentyPercentPerPax = "TwentyPercentPerPax",
  // TwentyPoundsPerPax = "TwentyPoundsPerPax",
  // -> Nov/Dec FTU Experiments
  // TwentyPoundsPerPax = "TwentyPoundsPerPax",
  // TenPercent = "TenPercent",
  // FivePercent = "FivePercent",
  // -> Feb/Apr FTU Experiments
  TenPercentOffer = "TenPercentOffer",
  TwentyPercentOffer = "TwentyPercentOffer",
}

const isOfferValid = (variant: FTU_VARIANTS) => {
  const TIMEZONE_OF_OFFER = "Europe/London";
  // Initial FTU Experiment
  // - Expiry on March 22 2024 (Expiry set to March 22st at 23:50)
  // April Draw FTU
  // - Expiry May 6 2024 (Expiry set to May 6th at 23:50)
  // May Promo FTU
  // - Expiry June 10 2024 (Expiry set to June 10rd at 23:50)
  // July/August Promo FTU
  // - Expiry August 12 2024 (Expiry set to August 12rd at 23:50)
  // Sept/Oct Promo FTU
  // - Expiry October 14 2024 (Expiry set to October 14th at 23:50)
  // Nov/Dec Promo FTU
  // - Expiry January 31 2025 (Expiry set to January 31st at 23:50 - originally Dec 31st but was extended)
  // Feb/Apr Promo FTU
  // - Expiry April 30 2025 (Expiry set to April 30th at 23:50)
  // - Update: New expiry June 22 2025
  // - Update: May 2025 promo - offer 20% for May 18, 2025 only
  // - Update: June 2025 promo - offer 10% for Dec 31, 2025 only
  const DATETIME_OF_EXPIRY =
    variant === FTU_VARIANTS.TwentyPercentOffer
      ? { year: 2026, month: 4, date: 18, hour: 23, minute: 50 }
      : { year: 2026, month: 11, date: 31, hour: 23, minute: 50 };

  const currentUkDayjsObj = dayjs().tz(TIMEZONE_OF_OFFER);
  const currentYear = currentUkDayjsObj.get("year");
  const currentMonth = currentUkDayjsObj.get("month");
  const currentDate = currentUkDayjsObj.get("date");
  const currentHour = currentUkDayjsObj.get("hour");
  const currentMinute = currentUkDayjsObj.get("minute");

  // dayjs method isBefore and isAfter did not operate
  // as expected during tested and would at times give inaccurate results
  if (currentYear < DATETIME_OF_EXPIRY.year) {
    return true;
  } else if (currentYear === DATETIME_OF_EXPIRY.year) {
    if (currentMonth < DATETIME_OF_EXPIRY.month) {
      return true;
    } else if (currentMonth === DATETIME_OF_EXPIRY.month) {
      if (currentDate < DATETIME_OF_EXPIRY.date) {
        return true;
      } else if (currentDate === DATETIME_OF_EXPIRY.date) {
        if (currentHour < DATETIME_OF_EXPIRY.hour) {
          return true;
        } else if (currentHour === DATETIME_OF_EXPIRY.hour) {
          if (currentMinute < DATETIME_OF_EXPIRY.minute) {
            return true;
          } else if (currentMinute === DATETIME_OF_EXPIRY.minute) {
            return true;
          }
        }
      }
    }
  }

  return false;
};

export const useFtuExperiment = () => {
  const variant = useGetExperimentVariant(FTU_EXPERIMENT_KEY) as FTU_VARIANTS;
  const isValid = isOfferValid(variant);
  const enabled = variant !== FTU_VARIANTS.control && isValid;
  return { enabled, variant };
};

export const mapFtuVariantToExpiry = {
  [FTU_VARIANTS.TenPercentOffer]: "2026-12-31",
  [FTU_VARIANTS.TwentyPercentOffer]: "2026-05-18",
};

export const mapFtuVariantToCopy = {
  [FTU_VARIANTS.TenPercentOffer]: {
    flightShopBanner: {
      title: "ftu.TenPercentOffer.flightShopBannerTitle",
    },
    flightReviewScreenBanner: {
      title: "ftu.TenPercentOffer.flightReviewBanner",
      priceBreakdownDiscount: "ftu.TenPercentOffer.priceBreakdownDiscount",
    },
    termsAndConditions: {
      infoModalTitle: "ftu.TenPercentOffer.infoModalTitle",
      infoModalValuePropCopy: "ftu.TenPercentOffer.infoModalValueProp",
      bookWithUber: "ftu.TenPercentOffer.bookWithUber",
      terms: "ftu.TenPercentOffer.termsAndConditionsCopy",
      linkCopy: "ftu.TenPercentOffer.termsLink",
    },
  },
  [FTU_VARIANTS.TwentyPercentOffer]: {
    flightShopBanner: {
      title: "ftu.TwentyPercentOffer.flightShopBannerTitle",
    },
    flightReviewScreenBanner: {
      title: "ftu.TwentyPercentOffer.flightReviewBanner",
      priceBreakdownDiscount: "ftu.TenPercentOffer.priceBreakdownDiscount",
    },
    termsAndConditions: {
      infoModalTitle: "ftu.TwentyPercentOffer.infoModalTitle",
      infoModalValuePropCopy: "ftu.TwentyPercentOffer.infoModalValueProp",
      bookWithUber: "ftu.TenPercentOffer.bookWithUber",
      terms: "ftu.TwentyPercentOffer.termsAndConditionsCopy",
      linkCopy: "ftu.TenPercentOffer.termsLink",
    },
  },
};

export const mapFtuVariantToCashbackCalculator: Record<
  FTU_VARIANTS,
  (price: FiatPrice, paxCount: number) => FiatPrice
> = {
  [FTU_VARIANTS.control]: (price: FiatPrice, paxCount: number) => ({
    value: 0, // Control, zero cashback
    currencyCode: price.currencyCode,
    currencySymbol: price.currencySymbol,
  }),
  [FTU_VARIANTS.TenPercentOffer]: (price: FiatPrice, paxCount: number) => {
    // Get 10% of total price and round to 2 decimal places
    const tenPercent = Math.round(price.value * 0.1 * 100) / 100;
    // No cap on cash back
    // const cashback = Math.min(tenPercent, 50);
    return {
      value: tenPercent,
      currencyCode: price.currencyCode,
      currencySymbol: price.currencySymbol,
    };
  },
  [FTU_VARIANTS.TwentyPercentOffer]: (price: FiatPrice) => {
    // Get 20% of total price and round to 2 decimal places
    const twentyPercent = Math.round(price.value * 0.2 * 100) / 100;
    // No cap on cash back
    // const cashback = Math.min(twentyPercent, 50);
    return {
      value: twentyPercent,
      currencyCode: price.currencyCode,
      currencySymbol: price.currencySymbol,
    };
  },
};
